import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { calculateTotalPages } from "../utils";

interface UseDeleteCheckInProps {
	userId: string | undefined;
	page: number;
	setPage: (page: number) => void;
	totalCount: number;
	pageSize: number;
	checkInsLength: number;
	refetch: () => void;
}

export const useDeleteCheckIn = ({
	userId,
	page,
	setPage,
	totalCount,
	pageSize,
	checkInsLength,
	refetch,
}: UseDeleteCheckInProps) => {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [checkInToDelete, setCheckInToDelete] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	const handleDeleteCheckIn = (checkInId: string, event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setCheckInToDelete(checkInId);
		setDeleteDialogOpen(true);
	};

	const confirmDeleteCheckIn = async () => {
		if (!checkInToDelete || !userId) return;

		setDeleting(true);
		try {
			// 🔒 SECURITY: Only allow users to delete their own check-ins
			const { error } = await supabase
				.from("daily_checkins")
				.delete()
				.eq("id", checkInToDelete)
				.eq("user_id", userId); // CRITICAL: Verify user ownership

			if (error) throw error;

			// Refresh the current page
			const totalPages = calculateTotalPages(totalCount, pageSize);

			// If deleting the last item on the last page, move back a page
			if (checkInsLength === 1 && page === totalPages - 1 && page > 0) {
				setPage(page - 1);
			} else {
				refetch();
			}

			setDeleteDialogOpen(false);
			setCheckInToDelete(null);
		} catch (error: any) {
			console.error("Error deleting check-in:", {
				message: error?.message,
				details: error?.details,
				hint: error?.hint,
				code: error?.code,
				fullError: error,
			});
			const errorMessage = error?.message || "Unknown error occurred";
			alert(`Failed to delete check-in: ${errorMessage}. Please try again.`);
		} finally {
			setDeleting(false);
		}
	};

	const cancelDeleteCheckIn = () => {
		setDeleteDialogOpen(false);
		setCheckInToDelete(null);
	};

	return {
		deleteDialogOpen,
		setDeleteDialogOpen,
		deleting,
		handleDeleteCheckIn,
		confirmDeleteCheckIn,
		cancelDeleteCheckIn,
	};
};
